import React, { useEffect, useState } from 'react';
import { CheckCircle, X } from 'lucide-react';

interface ToastProps {
  message: string;
  type?: 'success' | 'info' | 'error';
  duration?: number;
  onClose: () => void;
  isVisible: boolean;
}

const Toast: React.FC<ToastProps> = ({ 
  message, 
  type = 'success', 
  duration = 2500, 
  onClose, 
  isVisible 
}) => {
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimatingOut(false);
      const timer = setTimeout(() => {
        setIsAnimatingOut(true);
        setTimeout(onClose, 300);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible && !isAnimatingOut) return null;

  const bgColor = type === 'success' 
    ? 'bg-emerald-500' 
    : type === 'error' 
      ? 'bg-rose-500' 
      : 'bg-indigo-500';

  return (
    <div 
      className={`fixed bottom-36 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 px-5 py-3.5 rounded-2xl ${bgColor} text-white shadow-2xl transition-all duration-300 pointer-events-auto ${
        isAnimatingOut 
          ? 'opacity-0 translate-y-4 scale-95' 
          : 'opacity-100 translate-y-0 scale-100 animate-in slide-in-from-bottom-4'
      }`}
    >
      <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
        <CheckCircle size={16} className="text-white" />
      </div>
      <span className="text-[14px] font-bold">{message}</span>
      <button 
        onClick={() => {
          setIsAnimatingOut(true);
          setTimeout(onClose, 300);
        }}
        className="ml-1 w-6 h-6 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
      >
        <X size={12} />
      </button>
    </div>
  );
};

export default Toast;